/* Glinster live bracelet builder
   Single source of truth: `slots` - an array, each entry null or a charm.
   Everything else (price, counts, summary, cart payload) is computed from it. */

window.braceletBuilder = function () {
  return {
    sizes: [
      { label: 'XS', slots: 16, wrist: '5.5\u20136\u2033' },
      { label: 'S', slots: 17, wrist: '6\u20136.5\u2033' },
      { label: 'M', slots: 18, wrist: '6.5\u20137\u2033' },
      { label: 'L', slots: 19, wrist: '7\u20137.5\u2033' },
      { label: 'XL', slots: 20, wrist: '7.5\u20138\u2033' },
    ],
    minCustom: 10,
    maxCustom: 30,
    size: 'M',
    customCount: 18,
    slots: [],
    activeSlot: 0,
    charms: [],
    assembly: [],
    assemblyId: null,
    filter: 'all',
    colorFilter: 'all', // finish: 'all', 'Gold', 'Silver', ...
    query: '',
    message: '',
    adding: false,
    stock: {}, // variant id -> units in stock (null = unlimited)
    inCart: {}, // variant id -> units already in the cart outside this bracelet
    editing: null, // { id, keys } when editing a bracelet that's already in the cart

    init() {
      const data = JSON.parse(document.getElementById('bb-data').textContent);
      // One palette entry per variant, so "Letter Charm" with 26 variants = 26 charms
      this.charms = data.charms.flatMap((p) =>
        p.variants.map((v) => ({
          id: v.id,
          sku: v.sku || '',
          name: p.variants.length > 1 ? `${p.title} \u2013 ${v.title}` : p.title,
          price: v.price, // cents
          available: v.available,
          image: this.imageUrl((v.featured_image && v.featured_image.src) || p.featured_image),
          isBase: /base/i.test(p.type) || p.tags.some((t) => t.toLowerCase() === 'base'),
          tags: p.tags.map((t) => t.toLowerCase()),
          color: this.colorOf(p, v), // from the product's Color option, e.g. 'Gold'
        }))
      );
      this.assembly = (data.assembly || []).filter((v) => v.available);
      this.assemblyId = this.assembly.length ? this.assembly[0].id : null;
      this.setSize('M');
      this.stock = data.stock || {};
      this.tool = data.tool || null;
      if (this.tool) this.stock[this.tool.id] = this.tool.stock;
      // Don't start on "DIY + tool" when tools are sold out
      if (this.assemblyChoice && this.needsTool(this.assemblyChoice) && !this.toolAvailable) {
        const alt = this.assembly.find((v) => !this.needsTool(v));
        if (alt) this.assemblyId = alt.id;
      }
      const editId = new URLSearchParams(window.location.search).get('edit');
      this.loadCartCounts(editId);
      if (editId) this.loadFromCart(editId);
    },

    // ---------- edit a bracelet from the cart ----------
    get root() {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    },
    async loadFromCart(braceletId, keysOnly = false) {
      try {
        const cart = await (await fetch(this.root + 'cart.js')).json();
        const lines = cart.items.filter((it) => it.properties && it.properties._bracelet_id === braceletId);
        const main = lines.find((it) => it.properties._build);
        if (!main) return;
        const keys = lines.filter((it) => it === main || !it.parent_relationship).map((it) => it.key);
        const charmLines = lines.filter((it) => it !== main && it.parent_relationship);
        if (keysOnly) {
          this.editing = { id: braceletId, keys, main, charms: charmLines };
          return;
        }
        const build = JSON.parse(main.properties._build);
        const find = (ref) => this.charms.find((c) => c.sku === ref || String(c.id) === String(ref)) || null;
        const size = main.properties._size;
        const preset = this.sizes.find((s) => s.label === size && s.slots === build.length);
        this.size = preset ? preset.label : 'Custom';
        if (!preset) this.customCount = build.length;
        this.slots = build.map(find);
        this.assemblyId = main.variant_id;
        this.activeSlot = this.firstEmpty();
        // Nested charms disappear with their bracelet; older un-nested lines are removed explicitly
        this.editing = { id: braceletId, keys, main, charms: charmLines };
        if (this.slots.some((c) => !c)) this.message = 'Some charms from this bracelet are no longer available - pick a replacement for the empty links.';
      } catch (e) {
        this.message = 'We could not load that bracelet from your cart.';
      }
    },

    // ---------- computed ----------
    get filledCount() {
      return this.slots.filter(Boolean).length;
    },
    get emptyCount() {
      return this.slots.length - this.filledCount;
    },
    get isFull() {
      return this.emptyCount === 0;
    },
    get progress() {
      return Math.round((this.filledCount / this.slots.length) * 100);
    },
    get charmsTotal() {
      return this.slots.reduce((sum, c) => sum + (c ? c.price : 0), 0);
    },
    get assemblyChoice() {
      return this.assembly.find((v) => v.id === this.assemblyId) || null;
    },
    get total() {
      return this.charmsTotal + (this.assemblyChoice ? this.optionPrice(this.assemblyChoice) : 0);
    },
    // ---------- assembly tool ----------
    // "DIY + tool" adds the real Assembly Tool product (so tool stock is tracked)
    needsTool(variant) {
      return !!variant && /\+\s*tool/i.test(variant.title || '');
    },
    get toolAvailable() {
      if (!this.tool || !this.tool.available) return false;
      const stock = this.stock[this.tool.id];
      if (stock === null || stock === undefined) return true;
      return stock - (this.inCart[this.tool.id] || 0) > 0;
    },
    // What the option costs in total: its own price plus the tool when it includes one
    optionPrice(variant) {
      return variant.price + (this.needsTool(variant) && this.tool ? this.tool.price : 0);
    },
    get sizeLabel() {
      const preset = this.sizes.find((s) => s.label === this.size);
      return preset ? `${preset.label} (${preset.wrist})` : 'Custom';
    },
    // All base charms with stock (e.g. Gold and Silver), for the "fill the rest" buttons
    get baseCharms() {
      return this.charms.filter((c) => c.isBase && this.left(c) > 0);
    },
    baseLabel(charm) {
      const parts = charm.name.split(' \u2013 ');
      return parts.length > 1 ? parts[parts.length - 1] : charm.name;
    },
    get baseCharm() {
      return this.charms.find((c) => c.isBase && this.left(c) > 0) || null;
    },
    // Summary: one line per charm design with its quantity
    get summary() {
      const map = new Map();
      this.slots.forEach((c) => {
        if (!c) return;
        const line = map.get(c.id) || { charm: c, qty: 0 };
        line.qty++;
        map.set(c.id, line);
      });
      return [...map.values()];
    },
    get colors() {
      return [...new Set(this.charms.map((c) => c.color).filter(Boolean))];
    },
    get filters() {
      const skip = new Set(['builder', ...this.colors.map((c) => c.toLowerCase())]);
      const tags = new Set();
      this.charms.forEach((c) => c.tags.forEach((t) => !skip.has(t) && tags.add(t)));
      return ['all', ...tags];
    },
    get visibleCharms() {
      const q = this.query.trim().toLowerCase();
      return this.charms.filter(
        (c) =>
          (this.filter === 'all' || c.tags.includes(this.filter)) &&
          // charms without a colour (e.g. letters) show under every colour
          (this.colorFilter === 'all' || !c.color || c.color === this.colorFilter) &&
          (!q || c.name.toLowerCase().includes(q))
      );
    },
    colorOf(product, variant) {
      const names = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name) || '');
      const i = names.findIndex((n) => /^(colou?r|finish|metal)$/i.test(n));
      return i >= 0 && variant.options ? variant.options[i] : null;
    },
    get statusText() {
      if (this.filledCount === 0) return 'Tap a charm to start building';
      if (this.isFull) return 'Your bracelet is complete \u2726';
      return `${this.emptyCount} link${this.emptyCount === 1 ? '' : 's'} to go`;
    },

    // ---------- actions ----------
    setSize(label) {
      this.size = label;
      const preset = this.sizes.find((s) => s.label === label);
      this.resize(preset ? preset.slots : this.customCount);
    },
    stepCustom(delta) {
      if (this.size !== 'Custom') this.customCount = this.slots.length;
      this.customCount = Math.min(this.maxCustom, Math.max(this.minCustom, this.customCount + delta));
      this.size = 'Custom';
      this.resize(this.customCount);
    },
    resize(n) {
      const next = this.slots.slice(0, n);
      while (next.length < n) next.push(null);
      this.slots = next;
      this.activeSlot = this.firstEmpty();
    },
    // ---------- drag to swap ----------
    // Mouse: drag starts after moving 5px. Touch: press and hold ~250ms, so a quick swipe still scrolls.
    drag: null, // { from, over, x, y, startX, startY, image, touch, ready, started, pointerId }
    justDragged: false,

    dragStart(e, i) {
      if (!this.slots[i] || (e.pointerType === 'mouse' && e.button !== 0)) return;
      const touch = e.pointerType !== 'mouse';
      this.drag = {
        from: i, over: null, x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY,
        image: this.slots[i].image, touch, ready: !touch, started: false, pointerId: e.pointerId,
      };
      this._onMove = (ev) => this.dragMove(ev);
      this._onUp = (ev) => this.dragEnd(ev);
      this._onCancel = () => this.dragCleanup();
      // Blocks page scrolling only while a touch drag is actually running
      this._onTouchMove = (ev) => { if (this.drag && this.drag.started) ev.preventDefault(); };
      window.addEventListener('pointermove', this._onMove);
      window.addEventListener('pointerup', this._onUp);
      window.addEventListener('pointercancel', this._onCancel);
      window.addEventListener('touchmove', this._onTouchMove, { passive: false });
      if (touch) {
        this._holdTimer = setTimeout(() => {
          if (!this.drag) return;
          this.drag.ready = true;
          this.drag.started = true;
          this.drag.over = this.drag.from;
          if (navigator.vibrate) navigator.vibrate(10);
        }, 250);
      }
    },
    dragMove(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.pointerId) return;
      d.x = e.clientX;
      d.y = e.clientY;
      const moved = Math.hypot(d.x - d.startX, d.y - d.startY);
      if (!d.ready) {
        if (moved > 8) this.dragCleanup(); // finger moved before the hold finished: it's a scroll
        return;
      }
      if (!d.started) {
        if (moved < 5) return;
        d.started = true;
      }
      const el = document.elementFromPoint(d.x, d.y);
      const link = el && el.closest('[data-slot]');
      d.over = link ? Number(link.dataset.slot) : null;
      this.autoScroll(d.x);
    },
    dragEnd(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.pointerId) return;
      if (d.started) {
        if (d.over !== null && d.over !== d.from) {
          const moving = this.slots[d.from];
          this.slots[d.from] = this.slots[d.over]; // null if the target was empty = a move
          this.slots[d.over] = moving;
          this.activeSlot = this.firstEmpty();
        }
        this.justDragged = true; // swallow the click that follows the drop
        setTimeout(() => (this.justDragged = false), 0);
      }
      this.dragCleanup();
    },
    dragCleanup() {
      clearTimeout(this._holdTimer);
      window.removeEventListener('pointermove', this._onMove);
      window.removeEventListener('pointerup', this._onUp);
      window.removeEventListener('pointercancel', this._onCancel);
      window.removeEventListener('touchmove', this._onTouchMove);
      this.drag = null;
    },
    // Scroll the bracelet when dragging near its edges (long bracelets on small screens)
    autoScroll(x) {
      const row = this.$refs.bracelet;
      if (!row || row.scrollWidth <= row.clientWidth) return;
      const r = row.getBoundingClientRect();
      if (x < r.left + 40) row.scrollLeft -= 10;
      else if (x > r.right - 40) row.scrollLeft += 10;
    },
    linkClick(i) {
      if (this.justDragged) return;
      this.tapSlot(i);
    },

    // First tap selects a link (tapping a charm then replaces it); a second tap on the selected link removes its charm.
    tapSlot(i) {
      this.message = '';
      if (this.slots[i] && this.activeSlot === i) {
        this.slots[i] = null;
        return;
      }
      this.activeSlot = i;
    },
    // ---------- stock ----------
    // Charms already in the cart (loose, or in other bracelets) count against stock too
    async loadCartCounts(skipBraceletId) {
      try {
        const cart = await (await fetch(this.root + 'cart.js')).json();
        const counts = {};
        cart.items.forEach((it) => {
          if (skipBraceletId && it.properties && it.properties._bracelet_id === skipBraceletId) return;
          counts[it.variant_id] = (counts[it.variant_id] || 0) + it.quantity;
        });
        this.inCart = counts;
      } catch (e) {}
    },
    used(charm) {
      return this.slots.filter((c) => c && c.id === charm.id).length;
    },
    // How many more of this charm can go on the bracelet
    left(charm) {
      if (!charm.available) return 0;
      const stock = this.stock[charm.id];
      if (stock === null || stock === undefined) return Infinity;
      return stock - (this.inCart[charm.id] || 0) - this.used(charm);
    },
    stockNote(charm) {
      const n = this.left(charm);
      if (n <= 0) return this.used(charm) ? 'All in your bracelet' : 'Sold out';
      if (n <= 3) return `Only ${n} left`;
      return '';
    },

    place(charm) {
      const i = this.activeSlot ?? this.firstEmpty();
      if (i === null) {
        this.message = 'Your bracelet is full - tap a link to swap it out.';
        return;
      }
      if (this.slots[i] && this.slots[i].id === charm.id) return; // already there
      if (this.left(charm) <= 0) {
        this.message = this.used(charm)
          ? `That's every ${charm.name} we have - they're all on your bracelet.`
          : `Sorry, ${charm.name} is sold out.`;
        return;
      }
      this.message = '';
      this.slots[i] = charm;
      this.activeSlot = this.firstEmpty(i + 1);
    },
    removeOne(id) {
      for (let i = this.slots.length - 1; i >= 0; i--) {
        if (this.slots[i] && this.slots[i].id === id) {
          this.slots[i] = null;
          this.activeSlot = i;
          return;
        }
      }
    },
    fillWithBase(charm) {
      const base = charm || this.baseCharm;
      if (!base) return;
      let room = this.left(base);
      this.slots = this.slots.map((c) => {
        if (c || room <= 0) return c;
        room--;
        return base;
      });
      this.activeSlot = this.firstEmpty();
      this.message = this.isFull ? '' : `We only have enough ${base.name} to fill part of it - pick charms for the rest.`;
    },
    clearAll() {
      this.slots = this.slots.map(() => null);
      this.activeSlot = 0;
      this.message = '';
    },
    firstEmpty(from = 0) {
      let i = this.slots.findIndex((s, idx) => idx >= from && !s);
      if (i === -1) i = this.slots.findIndex((s) => !s); // wrap around
      return i === -1 ? null : i;
    },

    // One cart line per charm (so Shopify tracks stock per charm) + one bracelet line,
    // all sharing a hidden _bracelet_id so they can be grouped back together.
    async addToCart() {
      if (!this.isFull || !this.assemblyId || this.adding) return;
      if (this.needsTool(this.assemblyChoice) && !this.toolAvailable) {
        this.message = 'Sorry, the assembly tool is sold out - pick another option.';
        return;
      }
      this.adding = true;
      this.message = '';
      const braceletId = 'GL-' + Date.now().toString(36).toUpperCase();
      const design = this.slots.map((c, i) => `${i + 1}. ${c.name}`).join(' \u00B7 ');
      const bracelet = {
        id: this.assemblyId,
        quantity: 1,
        properties: {
          _size_label: `${this.sizeLabel} \u00B7 ${this.slots.length} links`,
          _design: design,
          _bracelet_id: braceletId,
          _size: this.size,
          _build: JSON.stringify(this.slots.map((c) => c.sku || c.id)),
        },
      };
      const root = this.root;
      const post = (url, body) =>
        fetch(root + url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
      // Put a bracelet (bracelet line + nested charm lines) into the cart
      const addNested = async (parentItem, charmItems) => {
        let r = await post('cart/add.js', { items: [parentItem] });
        let d = await r.json();
        if (!r.ok) throw new Error(d.description || d.message || 'Could not add to cart');
        const key = d.items[0].key;
        r = await post('cart/add.js', { items: charmItems.map((c) => ({ ...c, parent_line_key: key })) });
        d = await r.json();
        if (!r.ok) {
          await post('cart/change.js', { id: key, quantity: 0 });
          throw new Error(d.description || d.message || 'Could not add to cart');
        }
      };
      const charmItems = this.summary.map((line) => ({
        id: line.charm.id,
        quantity: line.qty,
        properties: { _bracelet_id: braceletId },
      }));
      if (this.needsTool(this.assemblyChoice) && this.tool) {
        charmItems.push({ id: this.tool.id, quantity: 1, properties: { _bracelet_id: braceletId, _bracelet_tool: '1' } });
      }
      if (this.editing) {
        // Remove the old version first so its charms don't count against stock; restore it if the new one fails
        const updates = {};
        this.editing.keys.forEach((k) => (updates[k] = 0));
        await post('cart/update.js', { updates });
        try {
          await addNested(bracelet, charmItems);
          window.location.href = root + 'cart';
        } catch (e) {
          const m = this.editing.main;
          await addNested(
            { id: m.variant_id, quantity: m.quantity, properties: m.properties },
            this.editing.charms.map((c) => ({ id: c.variant_id, quantity: c.quantity, properties: c.properties }))
          ).catch(() => {});
          await this.loadFromCart(this.editing.id, true); // pick up the restored lines' new keys
          this.message = e.message + ' Your original bracelet is still in your cart.';
          this.adding = false;
        }
        return;
      }
      try {
        // The bracelet line (assembly method) first, then its charms nested underneath it, so the cart
        // shows one bracelet and removing the bracelet removes its charms too.
        await addNested(bracelet, charmItems);
        window.location.href = root + 'cart';
      } catch (e) {
        this.message = e.message;
        this.adding = false;
      }
    },

    // ---------- helpers ----------
    money(cents) {
      return '$' + (cents / 100).toFixed(2);
    },
    tagLabel(t) {
      if (t === 'all') return 'All charms';
      if (t === 'y2k') return 'Y2K';
      return t.charAt(0).toUpperCase() + t.slice(1);
    },
    imageUrl(src) {
      if (!src) return '';
      const url = src.startsWith('//') ? 'https:' + src : src;
      return url + (url.includes('?') ? '&' : '?') + 'width=240';
    },
  };
};
